import tw from "tailwind-styled-components";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { FiImage, FiPlus } from "react-icons/fi";
import { Underline } from "../../Styles/PageStyles";

const InventoryImagesModal = ({
  images = [],
  currentImageIndex,
  setCurrentImageIndex,
  handleImageNavigation,
  setImageUpload,
}) => {
  const hasImages = images?.length > 0;
  const totalImages = images?.length || 0;

  // Show first 3 images + 1 Add tile = 4 slots
  const visibleImages = images?.slice(0, 3) || [];
  const currentImage = images?.[currentImageIndex];

  return (
    <div className="p-4">
      <TabTitle className="!mb-0 whitespace-nowrap">
        Inventory Media
        <Underline />
      </TabTitle>

      <HeroSection hasImage={hasImages}>
        {hasImages ? (
          <>
            {/* Main Image */}
            <ImageGallery>
              <MainImage hasImage={hasImages}>
                <img
                  src={currentImage}
                  alt={`Inventory ${currentImageIndex + 1}`}
                  className="w-full h-full object-cover transition-all duration-300"
                />

                {totalImages > 1 && (
                  <>
                    <NavButton left onClick={() => handleImageNavigation("prev")}>
                      <MdKeyboardArrowLeft />
                    </NavButton>

                    <NavButton right onClick={() => handleImageNavigation("next")}>
                      <MdKeyboardArrowRight />
                    </NavButton>
                  </>
                )}

                <ImageIndicator>
                  {images.map((_, idx) => (
                    <Dot
                      key={idx}
                      active={idx === currentImageIndex}
                      onClick={() => setCurrentImageIndex(idx)}
                    />
                  ))}
                </ImageIndicator>

                <ImageCountBadge>
                  {currentImageIndex + 1}/{totalImages}
                </ImageCountBadge>
              </MainImage>
            </ImageGallery>

            {/* Thumbnail Grid */}
            <PropertyHeader>
              <ThumbnailGrid>
                {visibleImages.map((img, idx) => {
                  const isActive = idx === currentImageIndex;

                  return (
                    <Thumbnail
                      key={idx}
                      active={isActive}
                      onClick={() => setCurrentImageIndex(idx)}
                    >
                      <img
                        src={img}
                        alt={`Thumbnail ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />

                      {isActive && (
                        <div className="absolute inset-0 ring-2 ring-primary-color rounded-lg" />
                      )}
                    </Thumbnail>
                  );
                })}

                {/* Always visible Add Image tile */}
                <AddImageTile onClick={() => setImageUpload(true)}>
                  <FiPlus size={22} className="mb-1 text-gray-500" />
                  <span className="text-xs font-medium text-gray-500">
                    Add Image
                  </span>
                </AddImageTile>
              </ThumbnailGrid>

              {/* More Images Badge */}
              {totalImages > 3 && (
                <div className="mt-2 text-xs text-gray-500 text-right">
                  +{totalImages - 3} more image
                  {totalImages - 3 > 1 ? "s" : ""}
                </div>
              )}
            </PropertyHeader>
          </>
        ) : (
          <EmptyHero>
            <FiImage size={50} className="text-gray-400 mb-3" />

            <h3 className="text-gray-600 font-semibold text-lg">
              No Images Available
            </h3>

            <p className="text-gray-400 text-sm mb-5">
              Add images to showcase your inventory
            </p>

            <button
              onClick={() => setImageUpload(true)}
              className="px-6 py-2 bg-gradient-to-r from-primary-color to-teal-400 text-white rounded-xl text-sm"
            >
              + Add Image
            </button>
          </EmptyHero>
        )}
      </HeroSection>
    </div>
  );
};

const HeroSection = tw.div`
  grid lg:grid-cols-5 gap-6 my-6 p-4 rounded-xl
  ${({ hasImage }) =>
    hasImage
      ? "bg-white"
      : "border-2 border-dashed border-gray-300 bg-gray-50"}
`;

const ImageGallery = tw.div`
  lg:col-span-3
`;

const MainImage = tw.div`
  relative rounded-2xl overflow-hidden
  flex items-center justify-center
  bg-gray-100
  ${({ hasImage }) =>
    hasImage
      ? "h-80 md:h-96"
      : "h-64 md:h-72"}
`;

const NavButton = tw.button`
  absolute top-1/2 -translate-y-1/2
  w-10 h-10 bg-white/90 backdrop-blur-sm
  rounded-full flex items-center justify-center
  text-2xl shadow-lg hover:scale-105
  transition-all z-10
  ${({ left }) => (left ? "left-4" : "right-4")}
`;

const ImageIndicator = tw.div`
  absolute bottom-4 left-1/2 -translate-x-1/2
  flex space-x-2 z-10
`;

const Dot = tw.button`
  w-2 h-2 rounded-full transition-all
  ${({ active }) =>
    active ? "bg-primary-color scale-125" : "bg-gray-300"}
`;

const ImageCountBadge = tw.div`
  absolute top-4 right-4
  bg-black/60 text-white
  px-3 py-1.5 rounded-full
  text-xs font-medium backdrop-blur-sm
`;

const PropertyHeader = tw.div`
  lg:col-span-2
`;

const ThumbnailGrid = tw.div`
  grid grid-cols-2 gap-2
`;

const Thumbnail = tw.div`
  relative h-24 md:h-44 rounded-xl overflow-hidden cursor-pointer
  border transition-all hover:border-primary-color/80
  ${({ active }) =>
    active ? "border-primary-color" : "border-gray-200"}
`;

const AddImageTile = tw.button`
  h-24 md:h-44 rounded-xl
  border-2 border-dashed border-gray-300
  flex flex-col items-center justify-center
  hover:bg-gray-50 transition-all
`;

const EmptyHero = tw.div`
  flex flex-col items-center justify-center text-center
  w-full h-72 col-span-5
`;

const TabTitle = tw.h2`
  text-lg text-black font-semibold
`;

export default InventoryImagesModal;